import React from 'react';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Typography } from 'Components/atoms';
import { TrainingDueInDaysField } from 'Components/molecules/TrainingDueInDaysField';
import ampyDefault from 'Assets/thumbnails/ampy_default.gif';
import { ModuleTrainingData } from 'Components/organisms/ComplianceTrainingScheduleModal/ScheduleSidePanel';
import { TrainingModuleState } from './types';
import { useI18n } from 'Hooks/useI18n';
import { formatLaunchCooldownExpiry, isModuleInLaunchCooldown } from './helpers';

interface TrainingModuleCardProps {
    module: TrainingModuleState;
    isGlobalDisabled: boolean;
    schedulesCount: number; // Used to toggle button text
    onToggleModule: (subjectId: string) => void;
    onOverdueChange: (subjectId: string, days: string) => void;
    onConfigureSchedule: (id: string, name: string, subject: string, data: ModuleTrainingData) => void;
    onLaunchTraining: (subjectId: string, name: string) => void;
}

export const TrainingModuleCard: React.FC<TrainingModuleCardProps> = ({
    module,
    isGlobalDisabled,
    schedulesCount,
    onToggleModule,
    onOverdueChange,
    onConfigureSchedule,
    onLaunchTraining,
}) => {
    const isRowDisabled = isGlobalDisabled || !module.enabled;
    const isInLaunchCooldown = isModuleInLaunchCooldown(module);
    const isLaunchButtonDisabled = isRowDisabled || module.isLaunching || isInLaunchCooldown;
    const showLaunchDisabledCursor = isRowDisabled || isInLaunchCooldown;
    const { t } = useI18n();

    const launchCooldownTooltip = (() => {
        if (!isInLaunchCooldown || module.launchCooldownExpiresAt === null) {
            return '';
        }
        const expiresAt = formatLaunchCooldownExpiry(module.launchCooldownExpiresAt);
        const rawTooltip = t(
            'COMPLIANCE_TRAINING_LAUNCH_NOW_COOLDOWN_TOOLTIP',
            'This training module is launching and cannot be launched again until {expiresAt}.',
        );
        return rawTooltip.includes('{expiresAt}')
            ? rawTooltip.replace('{expiresAt}', expiresAt)
            : `${rawTooltip} ${expiresAt}`;
    })();
    return (
        <div className="training-module-card">
            {/* 1. Thumbnail Section */}
            <div className="module-thumbnail-col">
                <div className="thumbnail-wrapper">
                    {module.thumbnail ? (
                        <img src={module.thumbnail} alt={module.name} />
                    ) : (
                        <div className="no-thumb-placeholder">
                            <img src={ampyDefault} alt="No thumbnail" />
                        </div>
                    )}
                </div>
            </div>

            {/* 2. Info Section */}
            <div className="module-info-col">
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <Typography variant="subtitle2" color="text-high-emphasis">
                        {module.name}
                    </Typography>
                </div>

                <Typography
                    variant="body3"
                    color="text-medium-emphasis"
                    className="module-description mt-1"
                    aria-label={module.description}
                >
                    {module.description}
                </Typography>

                <Typography variant="body3" color="text-high-emphasis" className="preview-link mt-2">
                    <a target="_blank" href={module.videoUrl} rel="noreferrer">
                        ⏯ Preview {module.name} Module Content
                    </a>
                </Typography>
            </div>

            {/* 3. Actions Section */}
            <div className="module-actions-col">
                {/* Toggle Section */}
                <div className="action-row toggle-row">
                    <Typography variant="caption1" color="text-medium-emphasis">
                        Enable Module
                    </Typography>
                    <Form.Check
                        type="switch"
                        checked={module.enabled}
                        onChange={() => onToggleModule(module.subject)}
                        disabled={isGlobalDisabled}
                    />
                </div>

                {/* Overdue Configuration Section */}
                <div className="action-row overdue-row">
                    <TrainingDueInDaysField
                        value={module.overdueInDays}
                        onChange={(days) => onOverdueChange(module.subject, days)}
                        disabled={isRowDisabled}
                        className="w-100"
                    />
                </div>

                {/* Schedule + Launch Buttons */}
                <div className="action-row launch-row gap-3">
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            isRowDisabled ? <Tooltip>{t('COMPLIANCE_TRAINING_ENABLE_TO_SCHEDULE')}</Tooltip> : <></>
                        }
                    >
                        <span className="flex-fill" style={isRowDisabled ? { cursor: 'not-allowed' } : undefined}>
                            <Button
                                variant="primary"
                                size="sm"
                                className="body1 w-100 text-text-black btn btn-primary-500"
                                onClick={() =>
                                    onConfigureSchedule(module.subject, module.name, module.subject, {
                                        description: module.description,
                                        videoUrl: module.videoUrl,
                                        thumbnailUrl: module.thumbnail ?? '',
                                        dueInDays: parseInt(module.overdueInDays, 10) ?? undefined,
                                    })
                                }
                                disabled={isRowDisabled}
                                style={isRowDisabled ? { pointerEvents: 'none' } : undefined}
                            >
                                {schedulesCount > 0 ? 'Manage Schedules' : 'Create Schedule'}
                            </Button>
                        </span>
                    </OverlayTrigger>
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            isRowDisabled ? (
                                <Tooltip>{t('COMPLIANCE_TRAINING_ENABLE_TO_LAUNCH_NOW')}</Tooltip>
                            ) : isInLaunchCooldown ? (
                                <Tooltip>{launchCooldownTooltip}</Tooltip>
                            ) : (
                                <></>
                            )
                        }
                    >
                        <span
                            className="flex-fill"
                            style={showLaunchDisabledCursor ? { cursor: 'not-allowed' } : undefined}
                        >
                            <Button
                                variant="primary"
                                size="sm"
                                className="body1 w-100 text-text-black btn btn-primary-500"
                                onClick={() => onLaunchTraining(module.subject, module.name)}
                                disabled={isLaunchButtonDisabled}
                                style={showLaunchDisabledCursor ? { pointerEvents: 'none' } : undefined}
                            >
                                {module.isLaunching ? 'Launching...' : 'Launch Now'}
                            </Button>
                        </span>
                    </OverlayTrigger>
                </div>
            </div>
        </div>
    );
};
